/* ==========================================================================
   CRTZ - LIGHT MODE TOGGLE
   Loaded from layout/theme.liquid via a single <script defer> tag.
   Paired with crtz-light-mode.css, which holds every colour this switches to.
   Delete that tag (and this file) to revert completely.

   Puts an icon beside Instagram in the social links. Clicking it adds or
   removes html.crtz-light and remembers the choice in localStorage.

   DARK IS THE DEFAULT. Nothing is stored until the customer presses the
   button, and an empty/unreadable store reads as dark - so every existing
   visitor sees exactly what they see today, and any failure of this script
   leaves the site in its current state rather than a half-painted one.

   --------------------------------------------------------------------------
   WHY THE CLASS IS APPLIED IN <head>, NOT HERE

   theme.liquid carries a two-line inline script that reads localStorage and
   sets the class BEFORE the first paint. It has to: this file is deferred, so
   by the time it runs the browser has already painted a black page, and a
   returning light-mode customer would see a black flash on every navigation.

   This file therefore never applies the stored value on load - it only reads
   it back to draw the button in the right state. If the two ever disagree,
   the inline script is the one that is right.

   --------------------------------------------------------------------------
   NOTES ON THIS THEME'S QUIRKS

     - .social-links is DUPLICATED, one copy per breakpoint, the same way
       #HeaderLogo is. insert() handles both; see the comment there.

     - This deliberately stays OUT of the header. crtz-sticky-header.js sizes
       the mobile header bar by measuring #ToggleMobileMenu,
       .site-header__cart and .crtz-logo-slot, and lifts them by --crtz-lift
       on top of per-icon offsets of its own. Anything else placed in that
       row has to reproduce those offsets by hand to sit on the same line,
       which couples this file to that one's internals for no real gain.

     - The cart drawer replaces its own markup wholesale on every cart
       update. The social links are outside the drawer, so the buttons
       survive - but insert() is idempotent per list regardless.

     - The Venn Apps native wrapper hides the theme's own chrome entirely.
       Same early return as crtz-sticky-header.js.
   ========================================================================== */
(function () {
  'use strict';

  var KEY   = 'crtz-theme';
  var LIGHT = 'light';
  var DARK  = 'dark';
  var CLASS = 'crtz-light';

  /* The Venn Apps native wrapper supplies its own chrome - stay out of it. */
  try {
    if (new URLSearchParams(location.search).get('sales_channel') === 'venn_apps') return;
  } catch (e) {}

  var root = document.documentElement;

  /* Private browsing on iOS throws on localStorage access rather than
     returning null, so every touch of it is wrapped. A store that cannot be
     read or written degrades to "works for this page view only", which is
     the right failure: the toggle still switches, it just doesn't persist. */
  function stored() {
    try { return localStorage.getItem(KEY); } catch (e) { return null; }
  }

  function store(v) {
    try { localStorage.setItem(KEY, v); } catch (e) {}
  }

  function isLight() {
    return root.classList.contains(CLASS);
  }

  /* Half-filled circle - the conventional "appearance" glyph, and one that
     reads at 20px without colour. The same icon means "switch appearance" in
     both directions, so there is no second state to draw.

     viewBox is 2 2 20 20, NOT 0 0 24 24. The circle is drawn on a 24-unit
     grid but only spans 20 of it (r=10 about 12,12), so on the full viewBox
     it renders at 20/24 of its box - about 16.7px in a 20px slot, visibly
     smaller than the 20px Instagram glyph beside it. Cropping the viewBox to
     the artwork makes the two match: measured 20.0 against Instagram's 19.7. */
  function icon() {
    var NS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '2 2 20 20');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    /* Outline. */
    var ring = document.createElementNS(NS, 'path');
    ring.setAttribute('d',
      'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16z');
    svg.appendChild(ring);

    /* Filled half. */
    var half = document.createElementNS(NS, 'path');
    half.setAttribute('d', 'M12 4a8 8 0 0 1 0 16z');
    svg.appendChild(half);

    return svg;
  }

  /* Plural. .social-links exists TWICE - see insert() below - so there can be
     more than one button on the page and every one of them has to restate the
     current mode when any of them is pressed. */
  var btns = [];

  function label() {
    var next = isLight() ? 'dark' : 'light';
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-label', 'Switch to ' + next + ' mode');
      btns[i].setAttribute('title', 'Switch to ' + next + ' mode');
      btns[i].setAttribute('aria-pressed', isLight() ? 'true' : 'false');
    }
  }

  function apply(light) {
    root.classList.toggle(CLASS, light);
    store(light ? LIGHT : DARK);
    label();
  }

  function build() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'crtz-theme-toggle';
    btn.appendChild(icon());
    btn.addEventListener('click', function () { apply(!isLight()); });
    btns.push(btn);
    return btn;
  }

  /* Beside the Instagram icon.

     .social-links exists TWICE, the same way the logo does: one copy inside
     .sidebar-nav .footer-side, which is the one that renders on desktop, and
     one in the site footer, which is the one that renders on mobile. The
     other is 0x0 at any given breakpoint.

     Rather than work out which is live - that flips on resize, and the
     measurement is only valid after layout - a button goes into BOTH. The
     hidden list hides its button along with everything else in it, so at most
     one is ever visible, and label() keeps the pair in step.

     Appended as an <li> so it inherits the list's own inline-block spacing
     instead of fighting it.

     Idempotent per list, so this is safe to retry. */
  function insert() {
    var lists = document.querySelectorAll('.social-links');
    if (!lists.length) return false;

    for (var i = 0; i < lists.length; i++) {
      if (lists[i].querySelector('.crtz-theme-toggle')) continue;
      var li = document.createElement('li');
      li.className = 'crtz-theme-toggle-item';
      li.appendChild(build());
      lists[i].appendChild(li);
    }
    label();
    return true;
  }

  function init() {
    /* The class is already correct - set in <head> before first paint. This
       only needs to catch the case where storage says light but the inline
       script never ran (it is wrapped in try/catch too). */
    if (stored() === LIGHT && !isLight()) root.classList.add(CLASS);

    if (!insert()) {
      /* The header section is server-rendered, so this should not happen -
         but retry once after load rather than silently shipping no button. */
      window.addEventListener('load', insert);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
